import { useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import Badge from '@mui/material/Badge';

import i18n from 'shared/lib/i18n';

import { formatReactSelectValue } from '../Forms/utils';

const BadgeComponent = ({ number, placeholder = i18n.t('title.status') }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
    }}
  >
    <Typography style={{ margin: 0, padding: 0 }}>{placeholder}</Typography>
    <Box ml="2rem" mr="1rem">
      <Badge badgeContent={number} color="primary" />
    </Box>
  </Box>
);

const MultipleSelectComponent = ({
  multi,
  options,
  onChange,
  selectValue,
  placeholder,
  width,
}) => {
  const [multiValue, setMultiValue] = useState(selectValue || []);

  const debouncedHandler = useRef(
    debounce((value) => {
      onChange(formatReactSelectValue(value, multi).toString());
    }, 700),
  ).current;

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    setMultiValue(value);
    debouncedHandler(value);
  };

  useEffect(() => {
    setMultiValue(selectValue);
  }, [selectValue]);

  useEffect(
    () => () => {
      debouncedHandler.cancel();
    },
    [debouncedHandler],
  );

  const MenuProps = {
    PaperProps: {
      style: {
        borderRadius: 10,
        boxShadow: 'none',
        border: '1px solid #CFCFCF',
        width,
      },
    },
  };

  return (
    <FormControl sx={{ minWidth: '100%', minHeight: '5rem' }} size="small">
      <Select
        multiple
        value={multiValue}
        onChange={handleChange}
        variant="outlined"
        displayEmpty
        renderValue={() => (
          <BadgeComponent
            number={multiValue.length}
            placeholder={placeholder}
          />
        )}
        MenuProps={MenuProps}
      >
        {options.map((name) => (
          <MenuItem key={name.label} value={name} disableGutters dense>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Checkbox
                checked={
                  !!formatReactSelectValue(multiValue, multi).includes(
                    name.value,
                  )
                }
              />
              <Typography>{name.label}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

MultipleSelectComponent.defaultProps = {
  multi: true,
  onChange: () => {},
  options: null,
};

MultipleSelectComponent.propTypes = {
  multi: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.instanceOf(Object),
  selectValue: PropTypes.instanceOf(Array),
  placeholder: PropTypes.string,
  width: PropTypes.string,
};

BadgeComponent.defaultProps = {
  number: 0,
};

BadgeComponent.propTypes = {
  number: PropTypes.number,
  placeholder: PropTypes.string,
};

export default MultipleSelectComponent;
